import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Modal, Spin } from "antd"

import AddVariableWizard from "features/DataImport/components/DatamapEditor/AddVariableWizard.component"
import BulkCodeChangeModal from "features/DataImport/components/DatamapEditor/BulkCodeChangeModal.component"
import DatamapFindings from "features/DataImport/components/DatamapEditor/DatamapFindings.component"
import DatamapGrid from "features/DataImport/components/DatamapEditor/DatamapGrid.component"
import { parseDatamapCsv, serializeDatamapCsv } from "features/DataImport/components/DatamapEditor/utils/datamapCsv"
import { addBlankRow, addRows } from "features/DataImport/components/DatamapEditor/utils/datamapEdits"
import { pendingFindings } from "features/DataImport/components/DatamapEditor/utils/pendingFindings"
import {
  STORED_LOOSE_HEADER_NOTE,
  STORED_NOT_UTF8_NOTE,
  storedBlankRowsNote,
} from "features/DataImport/components/DatamapEditor/utils/readMapFile"
import { validateDatamap } from "features/DataImport/components/DatamapEditor/utils/validateDatamap"
import { useDatamapEditor } from "features/DataImport/api/useDatamapEditor"
import { serverMessageOf } from "features/DataImport/utils/serverMessage"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { downloadCsvAsFile } from "utils/files/downloadUtils"
import { DatamapCheckReport, DatamapDoc } from "types/datamap.types"

interface DatamapEditorModalProps {
  open: boolean
  projectId: string
  owner: string
  projectName: string
  /** Text of a datamap the upload refused; edited here and saved as the project's datamap. */
  initialText?: string
  /** Why the upload refused it, shown above the findings. */
  note?: string
  onClose: () => void
  onSaved: (reimport: boolean) => void
}

/**
 * The modal's body height. Around it antd puts the 20px offset from the top, the content padding
 * (20px above and below), the title (32px) and a 24px margin below, so the whole modal fits the window.
 */
const BODY_HEIGHT = "calc(100vh - 120px)"

/** The variables a re-import would leave out: each has a code the datamap lacks or a value its type can't hold. */
const leftOutCount = (report: DatamapCheckReport): number =>
  new Set([...report.unknownCodes, ...report.badCells].map((finding) => finding.variable)).size

/**
 * Edits the project's import datamap. It opens on a check against the data file that lists what
 * the import would drop, each with a fix, above a grid of every row. Saving is refused while the
 * validator finds problems.
 */
const DatamapEditorModal: React.FC<DatamapEditorModalProps> = ({
  open,
  projectId,
  owner,
  projectName,
  initialText,
  note,
  onClose,
  onSaved,
}) => {
  const { loadDatamap, saveDatamap, checkDatamap } = useDatamapEditor()
  const [doc, setDoc] = useState<DatamapDoc | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [savedCsv, setSavedCsv] = useState("")
  const [loadNote, setLoadNote] = useState<string | null>(null)
  const [report, setReport] = useState<DatamapCheckReport | null>(null)
  const [checkNote, setCheckNote] = useState<string | null>(null)
  /** The datamap the shown report was made from, so the panel can say when edits came after it. */
  const [checkedCsv, setCheckedCsv] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  // Each check takes a number; only the latest may show its result, so a slow check can't
  // overwrite a later one (or land on a later opening of the editor).
  const latestCheck = useRef(0)
  const [isSaving, setIsSaving] = useState(false)
  const [search, setSearch] = useState("")
  const [wizardColumn, setWizardColumn] = useState<string | null>(null)
  const [isBulkOpen, setIsBulkOpen] = useState(false)

  /**
   * Checks `text` against the data file and shows the result, unless a later check has started
   * since. Returns the report, or null when the check could not run (its reason is shown).
   */
  const check = useCallback(
    async (text: string): Promise<DatamapCheckReport | null> => {
      const request = ++latestCheck.current
      const isLatest = (): boolean => request === latestCheck.current
      setIsChecking(true)
      try {
        const response = await checkDatamap(projectId, owner, text)
        if ("error" in response) {
          if (isLatest()) {
            setReport(null)
            setCheckNote(`The check could not read this datamap: ${response.error}`)
          }
          return null
        }
        if (isLatest()) {
          setReport(response)
          setCheckedCsv(text)
          setCheckNote(null)
        }
        return response
      } catch (error) {
        if (isLatest()) {
          setReport(null)
          setCheckNote(serverMessageOf(error, "The datamap could not be checked against the data file."))
        }
        return null
      } finally {
        if (isLatest()) setIsChecking(false)
      }
    },
    [checkDatamap, projectId, owner]
  )

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async (): Promise<void> => {
      // A check still running from an earlier opening must not land on this one.
      latestCheck.current++
      setIsChecking(false)
      setDoc(null)
      setLoadError(null)
      setLoadNote(null)
      setReport(null)
      setCheckNote(null)
      setCheckedCsv(null)
      setSearch("")
      try {
        const loaded =
          initialText !== undefined
            ? { text: initialText, encoding: "utf-8" as const }
            : await loadDatamap(projectId, owner)
        if (cancelled) return
        const parsed = parseDatamapCsv(loaded.text)
        if (!parsed.ok) {
          setLoadError(parsed.error)
          return
        }
        const csv = serializeDatamapCsv(parsed.doc)
        const stored = initialText === undefined
        // The engine can't read a stored map with an empty row or a header not written exactly.
        const unreadable = stored && (parsed.blankRows > 0 || !parsed.headerExact)
        setDoc(parsed.doc)
        // A refused upload is not on the server yet, a stored Windows-1252 datamap must be written
        // back as UTF-8, and a stored map the engine can't read must be written again, so all of
        // these start out unsaved.
        setSavedCsv(stored && loaded.encoding === "utf-8" && !unreadable ? csv : "")
        const notes = [
          loaded.encoding === "windows-1252" ? STORED_NOT_UTF8_NOTE : null,
          stored && parsed.blankRows > 0 ? storedBlankRowsNote(parsed.blankRows) : null,
          stored && !parsed.headerExact ? STORED_LOOSE_HEADER_NOTE : null,
        ].filter((text): text is string => text !== null)
        setLoadNote(notes.length > 0 ? notes.join(" ") : null)
        void check(csv)
      } catch (error) {
        if (!cancelled) setLoadError(serverMessageOf(error, "The datamap could not be loaded."))
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // Load once per opening; check and loadDatamap are stable for a given project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialText, projectId, owner])

  const issues = useMemo(() => (doc ? validateDatamap(doc) : []), [doc])
  const findings = useMemo(() => (report && doc ? pendingFindings(report, doc) : null), [report, doc])
  const csv = useMemo(() => (doc ? serializeDatamapCsv(doc) : ""), [doc])
  const isDirty = doc !== null && csv !== savedCsv

  /** Stores `text` as the project's datamap, then lets the page re-import when asked. */
  const commit = async (text: string, reimport: boolean): Promise<void> => {
    setIsSaving(true)
    try {
      await saveDatamap(projectId, owner, text)
      setSavedCsv(text)
      notify.toast.success("Datamap saved")
      onSaved(reimport)
    } catch (error) {
      notify.error("The datamap was not saved", serverMessageOf(error, "Unknown error"))
    } finally {
      setIsSaving(false)
    }
  }

  const save = async (reimport: boolean): Promise<void> => {
    if (!doc) return
    if (issues.length > 0) {
      notify.toast.error(`Fix the ${issues.length} problem${issues.length === 1 ? "" : "s"} listed before saving.`)
      return
    }
    if (!reimport) return commit(csv, false)

    // The findings panel can't see everything a re-import would drop (a code changed everywhere in
    // questions the check never flagged, a category row linked to a text column, codes past those
    // listed), so the datamap is checked as it will be saved before it replaces the data.
    setIsSaving(true)
    const fresh = await check(csv)
    setIsSaving(false)
    const leftOut = fresh ? leftOutCount(fresh) : 0
    if (leftOut === 0) return commit(csv, true)
    Modal.confirm({
      title: "Some data would be left out of the import",
      content: `${leftOut} question${leftOut === 1 ? "" : "s"} would not import as the datamap stands. Re-import anyway, or save the datamap only?`,
      okText: "Re-import anyway",
      cancelText: "Save only",
      onOk: () => commit(csv, true),
      onCancel: () => commit(csv, false),
    })
  }

  const close = (): void => {
    if (!isDirty) {
      onClose()
      return
    }
    Modal.confirm({
      title: "Discard your changes to the datamap?",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: onClose,
    })
  }

  return (
    <Modal
      open={open}
      onCancel={close}
      width="95vw"
      style={{ maxWidth: 1600, top: 20 }}
      styles={{ body: { height: BODY_HEIGHT, overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title="Datamap editor"
      footer={null}
      maskClosable={false}
      destroyOnHidden
    >
      {(note ?? loadNote) && <Alert className="mb-3" type="warning" showIcon message={note ?? loadNote} />}
      {loadError && <Alert type="error" showIcon message="This datamap can't be edited here" description={loadError} />}
      {!doc && !loadError && (
        <div className="flex flex-1 items-center justify-center p-8">
          <Spin />
        </div>
      )}
      {doc && (
        // The toolbar stays put, the findings scroll in up to a third of the window and the grid
        // scrolls in what is left, so a large datamap never scrolls the page.
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-600">
              {doc.rows.length} row{doc.rows.length === 1 ? "" : "s"}
            </span>
            <DataQButton type="secondary" onClick={() => void check(csv)} isLoading={isChecking}>
              Check against the data file
            </DataQButton>
            <DataQButton type="secondary" onClick={() => setIsBulkOpen(true)}>
              Change a code everywhere
            </DataQButton>
            <DataQButton
              type="secondary"
              onClick={() => {
                setDoc(addBlankRow(doc))
                setSearch("")
              }}
            >
              Add row
            </DataQButton>
            <DataQButton type="secondary" onClick={() => downloadCsvAsFile(csv, `${projectName || "datamap"}.map.csv`)}>
              Download
            </DataQButton>
            <div className="ml-auto flex gap-2">
              <DataQButton type="secondary" onClick={() => void save(false)} isLoading={isSaving}>
                Save
              </DataQButton>
              <DataQButton type="primary" onClick={() => void save(true)} isLoading={isSaving}>
                Save and re-import
              </DataQButton>
            </div>
          </div>

          <div className="max-h-[33vh] overflow-y-auto">
            <DatamapFindings
              doc={doc}
              issues={issues}
              findings={findings}
              checkNote={checkNote}
              checkIsStale={csv !== checkedCsv}
              onChange={setDoc}
              onShowRow={setSearch}
              onAddVariable={setWizardColumn}
            />
          </div>
          <DatamapGrid doc={doc} issues={issues} search={search} onSearch={setSearch} onChange={setDoc} />

          <AddVariableWizard
            open={wizardColumn !== null}
            columns={findings?.unmappedColumns ?? []}
            initialColumn={wizardColumn}
            doc={doc}
            onAdd={(rows) => {
              setDoc(addRows(doc, rows))
              setWizardColumn(null)
            }}
            onCancel={() => setWizardColumn(null)}
          />
          <BulkCodeChangeModal
            open={isBulkOpen}
            doc={doc}
            onApply={(result) => {
              setDoc(result.doc)
              setIsBulkOpen(false)
              notify.toast.success(`Changed ${result.changed.length} question${result.changed.length === 1 ? "" : "s"}`)
            }}
            onCancel={() => setIsBulkOpen(false)}
          />
        </div>
      )}
    </Modal>
  )
}

export default DatamapEditorModal
